/**
 * Statement 多文件分离辅助工具函数
 */

import {
  EnhancedStatement,
  TargetFile,
  StatementCategory,
  DEFAULT_TARGET_FILE,
  getImportPathFromTargetFile,
} from '../types/statements';

/**
 * 根据 category 推断默认的 targetFile
 */
const CATEGORY_TO_TARGET_FILE: Record<StatementCategory, TargetFile> = {
  service: 'services.ts',
  hook: 'hooks.ts',
  state: 'store.ts',
  store: 'store.ts',
  constant: 'constants.ts',
  util: 'utils.ts',
  effect: 'index.tsx',
  callback: 'index.tsx',
  component: 'index.tsx',
  schema: 'schema.tsx',
};

/**
 * 导入信息结构
 */
export interface ImportSpec {
  defaultImport: string | null;
  namespaceImport: string | null;
  namedImports: Set<string>;
}

/**
 * 内部依赖映射类型
 * targetFile -> Map<fromFile, Set<importNames>>
 */
export type InternalDepsMap = Map<TargetFile, Map<TargetFile, Set<string>>>;

/**
 * 根据 statement 特性推断 targetFile
 *
 * 规则：
 * 1. 如果有明确指定的 targetFile，使用它
 * 2. 如果有 category，根据 category 推断
 * 3. 否则默认使用 index.tsx
 *
 * 注意：private 字段控制的是代码在文件内的位置（组件内部 vs 模块级别），
 * 而不是决定放到哪个文件。没有 targetFile 的 statement 默认都放在 index.tsx。
 *
 * @param stmt - 增强的 statement
 * @returns 推断的目标文件
 */
export function inferTargetFile(stmt: EnhancedStatement): TargetFile {
  // 1. 有明确指定的 targetFile
  if (stmt.targetFile) {
    return stmt.targetFile;
  }

  // 2. 如果有 category，根据 category 推断
  if (stmt.category) {
    return CATEGORY_TO_TARGET_FILE[stmt.category] || DEFAULT_TARGET_FILE;
  }

  // 3. 默认使用 index.tsx
  return DEFAULT_TARGET_FILE;
}

/**
 * 分析 statements 的跨文件依赖关系
 * 根据 internalRefs 和 references 字段确定哪些文件需要从其他文件导入什么
 *
 * @param statements - 增强的 statement 列表
 * @returns 内部依赖映射
 */
export function analyzeInternalDependencies(
  statements: EnhancedStatement[],
): InternalDepsMap {
  const result: InternalDepsMap = new Map();
  const stmtMap = new Map(statements.map((s) => [s.id, s]));

  // 1. 处理 internalRefs（正向依赖：A 引用了 B）
  for (const stmt of statements) {
    if (!stmt.internalRefs || stmt.internalRefs.length === 0) continue;

    const targetFile: TargetFile = inferTargetFile(stmt);

    for (const refId of stmt.internalRefs) {
      const refStmt = stmtMap.get(refId);
      if (!refStmt) continue;

      const refTargetFile: TargetFile = inferTargetFile(refStmt);

      // 同文件引用不需要导入
      if (refTargetFile === targetFile) continue;

      // 初始化 targetFile 的依赖 Map
      if (!result.has(targetFile)) {
        result.set(targetFile, new Map());
      }
      const fileImports = result.get(targetFile)!;

      // 初始化从 refTargetFile 导入的名称集合
      if (!fileImports.has(refTargetFile)) {
        fileImports.set(refTargetFile, new Set());
      }

      // 添加被引用 statement 的所有名称
      refStmt.names.forEach((name) => fileImports.get(refTargetFile)!.add(name));
    }
  }

  // 2. 处理 references（反向依赖：B 被组件属性引用，说明 index.tsx 中的 JSX 使用了该 statement）
  // references 格式: ["nodeId.ComponentName.propName"]，代表 JSX 中某节点的某属性使用了该 statement
  // 这类引用来自 componentTree，不是来自 statements，因此无法通过 stmtMap 查找引用方
  // 但可以确定：只要有这种格式的 references，index.tsx 就使用了该 statement
  const INDEX_FILE: TargetFile = 'index.tsx';
  const componentRefPattern = /^[^.]+\.[^.]+\.[^.]+$/;

  for (const stmt of statements) {
    if (!stmt.references || stmt.references.length === 0) continue;

    const stmtTargetFile: TargetFile = inferTargetFile(stmt);

    // 只处理不在 index.tsx 的 statement（同文件无需导入）
    if (stmtTargetFile === INDEX_FILE) continue;

    // 检查是否有任何 reference 是组件属性引用格式
    const hasComponentRef = stmt.references.some((ref) => componentRefPattern.test(ref));
    if (!hasComponentRef) continue;

    // index.tsx 需要从 stmtTargetFile 导入该 statement 的所有 names
    if (!result.has(INDEX_FILE)) {
      result.set(INDEX_FILE, new Map());
    }
    const fileImports = result.get(INDEX_FILE)!;

    if (!fileImports.has(stmtTargetFile)) {
      fileImports.set(stmtTargetFile, new Set());
    }

    stmt.names.forEach((name) => fileImports.get(stmtTargetFile)!.add(name));
  }

  return result;
}

/**
 * 根据 internalRefs 自动推断需要导出的 statements
 * 如果一个 statement 被其他文件的 statement 引用，则需要导出
 *
 * @param statements - 增强的 statement 列表
 * @returns 需要导出的 statement id 集合
 */
export function inferExportedStatements(
  statements: EnhancedStatement[],
): Set<string> {
  const exportedIds = new Set<string>();
  const stmtMap = new Map(statements.map((s) => [s.id, s]));

  for (const stmt of statements) {
    // 已明确标记为导出
    if (stmt.exported) {
      exportedIds.add(stmt.id);
      continue;
    }

    // 检查该 statement 是否引用了其他文件的 statement
    if (!stmt.internalRefs) continue;

    for (const refId of stmt.internalRefs) {
      const refStmt = stmtMap.get(refId);
      if (!refStmt) continue;

      const stmtFile: TargetFile = inferTargetFile(stmt);
      const refFile: TargetFile = inferTargetFile(refStmt);

      // 被引用者在不同文件，需要导出
      if (stmtFile !== refFile) {
        exportedIds.add(refId);
      }
    }
  }

  return exportedIds;
}

/**
 * 生成内部导入语句
 *
 * @param fromFile - 源文件路径
 * @param importNames - 要导入的名称列表
 * @returns 导入语句字符串
 */
export function generateInternalImportStatement(
  fromFile: TargetFile,
  importNames: string[],
): string {
  const importPath = getImportPathFromTargetFile(fromFile);
  const names = importNames.join(', ');
  return `import { ${names} } from '${importPath}';`;
}

/**
 * 按 targetFile 分组 statements
 * 使用 inferTargetFile 推断目标文件（考虑 private 字段）
 *
 * @param statements - 增强的 statement 列表
 * @returns 按目标文件分组的 Map
 */
export function groupStatementsByTargetFile(
  statements: EnhancedStatement[],
): Map<TargetFile, EnhancedStatement[]> {
  const grouped = new Map<TargetFile, EnhancedStatement[]>();

  for (const stmt of statements) {
    const targetFile: TargetFile = inferTargetFile(stmt);
    if (!grouped.has(targetFile)) {
      grouped.set(targetFile, []);
    }
    grouped.get(targetFile)!.push(stmt);
  }

  return grouped;
}

/**
 * 收集一组 statements 的外部依赖
 *
 * @param statements - 增强的 statement 列表
 * @returns 外部依赖映射
 */
export function collectExternalDependencies(
  statements: EnhancedStatement[],
): Record<string, ImportSpec> {
  const imports: Record<string, ImportSpec> = {};

  for (const stmt of statements) {
    if (!stmt.dependencies) continue;

    for (const dep of stmt.dependencies) {
      if (!dep.package) continue;

      if (!imports[dep.package]) {
        imports[dep.package] = {
          defaultImport: null,
          namespaceImport: null,
          namedImports: new Set(),
        };
      }

      if (dep.default) {
        imports[dep.package].defaultImport = dep.importName;
      } else if (dep.namespace) {
        imports[dep.package].namespaceImport = dep.importName;
      } else {
        imports[dep.package].namedImports.add(dep.importName);
      }
    }
  }

  return imports;
}

/**
 * 生成外部导入语句
 *
 * @param imports - 外部依赖映射
 * @returns 导入语句字符串
 */
export function generateExternalImportStatements(
  imports: Record<string, ImportSpec>,
): string {
  const lines: string[] = [];

  for (const [pkg, spec] of Object.entries(imports)) {
    if (!pkg) continue;

    if (spec.defaultImport) {
      lines.push(`import ${spec.defaultImport} from '${pkg}';`);
    }
    if (spec.namespaceImport) {
      lines.push(`import * as ${spec.namespaceImport} from '${pkg}';`);
    }
    if (spec.namedImports.size > 0) {
      const names = Array.from(spec.namedImports).join(', ');
      lines.push(`import { ${names} } from '${pkg}';`);
    }
  }

  return lines.join('\n');
}

/**
 * 为代码添加 export 关键字
 * 支持 const/let/var/function/class/async function 声明
 *
 * @param source - 源代码，可以是字符串或字符串数组
 * @returns 添加了 export 的代码，类型与输入保持一致
 */
export function addExportKeyword(source: string | string[]): string | string[] {
  const isStringArray = Array.isArray(source);
  const content = isStringArray ? source.join('\n') : source;
  const trimmed = content.trim();

  // 已经有 export 关键字
  if (trimmed.startsWith('export ')) {
    return source;
  }

  // 匹配各种声明模式
  const patterns = [
    /^(const\s+)/,
    /^(let\s+)/,
    /^(var\s+)/,
    /^(function\s+)/,
    /^(class\s+)/,
    /^(async\s+function\s+)/,
  ];

  for (const pattern of patterns) {
    if (pattern.test(trimmed)) {
      const exported = trimmed.replace(pattern, 'export $1');
      // 保持输入类型一致
      return isStringArray ? exported.split('\n') : exported;
    }
  }

  // 无法识别的模式，原样返回
  return source;
}
